import React, { useState, useEffect, useCallback } from "react";
import { PageSection, Surface } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Building2, Save, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Branches() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/branches");
      // sort by current sort_order (fallback 9999) then name
      const rows = (res.data || []).map((b, idx) => ({
        ...b,
        // If unset, assign the current index+1 so the user sees an ordered baseline.
        sort_order: b.sort_order ?? (idx + 1) * 10,
      }));
      setBranches(rows);
      setDirty(false);
    } catch (err) {
      toast.error("Failed to load branches");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRow = (id, patch) => {
    setBranches((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
    setDirty(true);
  };

  // Toggle active state — commit immediately (single-row action, no batch save needed).
  const toggleActive = async (b) => {
    const next = !b.is_active;
    updateRow(b.id, { is_active: next });
    try {
      await api.put(`/branches/${b.id}`, { is_active: next });
      toast.success(`${b.name} is now ${next ? "active" : "inactive"}`);
    } catch (err) {
      // revert on failure
      updateRow(b.id, { is_active: !next });
      toast.error(err?.response?.data?.detail || "Failed to update branch");
    }
  };

  // Persist all sort_order changes in one bulk call.
  const saveOrder = async () => {
    setSaving(true);
    try {
      const orders = Object.fromEntries(
        branches.map((b) => [b.id, Number.isFinite(b.sort_order) ? b.sort_order : 9999])
      );
      await api.post("/branches/reorder", { orders });
      toast.success("Branch order saved");
      setDirty(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  // Sort branches by sort_order for display (asc) — makes the table match Stock Monitoring order.
  const sorted = [...branches].sort((a, b) => {
    const oa = a.sort_order ?? 9999;
    const ob = b.sort_order ?? 9999;
    return oa - ob || (a.name || "").localeCompare(b.name || "");
  });

  const activeCount = branches.filter((b) => b.is_active).length;

  return (
    <div className="space-y-6" data-testid="branches-page">
      <PageSection
        title="Branch Settings"
        description={`${branches.length} branches · ${activeCount} active. Deactivated branches are hidden from Stock Monitoring and excluded from stock totals.`}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-9" data-testid="branches-reload">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1.5">Reload</span>
            </Button>
            <Button
              onClick={saveOrder}
              disabled={!dirty || saving}
              className="h-9 bg-[#9B1C1C] hover:bg-[#7F1D1D]"
              data-testid="branches-save-order"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save order
            </Button>
          </div>
        }
      >
        <Surface padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="px-4 py-3 w-12">#</th>
                  <th className="px-4 py-3 w-32">Order</th>
                  <th className="px-4 py-3">Branch Name</th>
                  <th className="px-4 py-3 w-40">Code</th>
                  <th className="px-4 py-3 w-32">Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">Loading branches…</td></tr>
                )}
                {!loading && sorted.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">No branches — run a Full Sync first.</td></tr>
                )}
                {!loading && sorted.map((b, idx) => (
                  <tr key={b.id} className={b.is_active ? "" : "bg-gray-50/70 text-gray-500"} data-testid={`branch-row-${b.code}`}>
                    <td className="px-4 py-3 text-gray-400 font-mono text-xs">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        value={b.sort_order}
                        onChange={(e) => updateRow(b.id, { sort_order: Number(e.target.value) })}
                        className="h-8 w-24 text-sm"
                        data-testid={`branch-order-${b.code}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className={b.is_active ? "font-medium text-gray-900" : "font-medium"}>{b.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{b.code}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!!b.is_active}
                          onCheckedChange={() => toggleActive(b)}
                          data-testid={`branch-active-${b.code}`}
                        />
                        <span className={`text-xs ${b.is_active ? "text-emerald-700 font-medium" : "text-gray-500"}`}>
                          {b.is_active ? "Active" : "Deactivated"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Surface>
        <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
          Tip: type new numbers in the <b>Order</b> column then click <b>Save order</b>. Stock Monitoring, Dashboard,
          and CSV exports respect this order and hide deactivated branches automatically.
        </p>
      </PageSection>
    </div>
  );
}
